import { lazySelector } from '@phx-styleguides/core';

export interface ScrollSpyOptions {
  /**
   * Selector for anchor elements
   */
  anchorSelector?: string;

  /**
   * The root element for intersection observer (null = viewport)
   */
  root?: Element | null;

  /**
   * Root margin for intersection observer
   */
  rootMargin?: string;

  /**
   * Threshold for intersection observer
   */
  threshold?: number | number[];

  /**
   * CSS class to add to active anchor elements
   */
  activeClass?: string;

  /**
   * Whether to add aria-current attribute for accessibility
   */
  useAriaCurrent?: boolean;
}

export class ScrollSpy {
  private observer: IntersectionObserver | null = null;
  private anchorElements: NodeListOf<HTMLElement> | null = null;
  private targetElements: HTMLElement[] = [];
  private options: Required<ScrollSpyOptions>;

  constructor(
    private container: string | HTMLElement,
    options: ScrollSpyOptions = {},
  ) {
    this.options = {
      anchorSelector: '[data-anchor-target]',
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0,
      activeClass: 'is-active',
      useAriaCurrent: true,
      ...options,
    };

    this.init();
  }

  private init() {
    const containerEl = lazySelector(this.container);
    this.anchorElements = containerEl.querySelectorAll(this.options.anchorSelector);

    this.setupScrollSpy();
  }

  private setupScrollSpy() {
    if (!this.anchorElements?.length) {
      return;
    }

    this.targetElements = [];

    this.anchorElements.forEach((anchor) => {
      const targetSelector = anchor.dataset.anchorTarget;
      if (targetSelector) {
        const target = document.querySelector(targetSelector) as HTMLElement;
        if (target) {
          this.targetElements.push(target);
        }
      }
    });

    if (this.targetElements.length === 0) {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        this.handleIntersection(entries);
      },
      {
        root: this.options.root,
        rootMargin: this.options.rootMargin,
        threshold: this.options.threshold,
      },
    );

    this.targetElements.forEach((target) => {
      this.observer?.observe(target);
    });
  }

  private handleIntersection(entries: IntersectionObserverEntry[]) {
    const visibleEntries = entries.filter((entry) => entry.isIntersecting);

    if (visibleEntries.length === 0) {
      return;
    }

    const mostVisible = visibleEntries.reduce((prev, current) =>
      prev.intersectionRatio > current.intersectionRatio ? prev : current,
    );

    this.updateActiveStates(mostVisible.target as HTMLElement);
  }

  private updateActiveStates(activeTarget: HTMLElement) {
    if (!this.anchorElements) {
      return;
    }

    const activeId = activeTarget.id ? `#${activeTarget.id}` : null;

    this.anchorElements.forEach((anchor) => {
      const isActive = anchor.dataset.anchorTarget === activeId;

      anchor.classList.toggle(this.options.activeClass, isActive);

      if (this.options.useAriaCurrent) {
        if (isActive) {
          anchor.setAttribute('aria-current', 'true');
        } else {
          anchor.removeAttribute('aria-current');
        }
      }
    });
  }

  public destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}
